import { createFileRoute, Link } from "@tanstack/react-router";
import { DarkCtaSection } from "@/components/dark-cta-section";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const pageTitle = "Salesforce Procurement & Commercial Advisory | SaaSed.co";
const pageDesc =
  "SaaSed is a specialist Salesforce procurement and commercial advisory company. We work with enterprise buyers on Salesforce renewals, contract negotiation, licensing decisions, commercial strategy and cost optimisation — with the aim of improving commercial control and removing cost that serves no purpose.";
const searchDescription = "Independent Salesforce procurement advice for enterprise buyers navigating renewals, licensing, contract terms and negotiations.";

function ArrowIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

const faqData = [
  {
    question: "What does a Salesforce procurement advisor do?",
    answer:
      "A Salesforce procurement advisor sits on the buyer's side of the table. The work covers reading the current agreement in commercial terms, mapping entitlements against real usage, benchmarking pricing, setting a target outcome, and supporting the buyer through the negotiation itself — including the internal alignment between procurement, finance and IT.",
  },
  {
    question: "When should a company start preparing for a Salesforce renewal?",
    answer:
      "Preparation should begin well before the renewal window opens, while usage data can still be gathered and licence counts can still be changed. Starting late narrows the options to accepting or resisting a proposal that has already been shaped by the vendor.",
  },
  {
    question: "Does SaaSed specialise exclusively in Salesforce?",
    answer:
      "Salesforce is our specialisation. We work on the commercial and licensing side of Salesforce agreements — including SELA and AELA structures — rather than on implementation or technical delivery.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://saased.co/salesforce-procurement#webpage",
      url: "https://saased.co/salesforce-procurement",
      name: "Salesforce Procurement & Commercial Advisory",
      description: pageDesc,
      isPartOf: { "@id": "https://saased.co/#website" },
      about: { "@id": "https://saased.co/#organization" },
      inLanguage: "en",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://saased.co/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Salesforce Procurement & Commercial Advisory",
          item: "https://saased.co/salesforce-procurement",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: faqData.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

type Group = { heading: string; articles: { slug: string; title: string }[] };

const insightGroups: Group[] = [
  {
    heading: "Salesforce Renewals & Negotiation",
    articles: [
      {
        slug: "what-salesforce-customers-should-ask-before-renewal",
        title: "What Salesforce Customers Should Ask Before Renewal",
      },
      {
        slug: "why-contract-review-should-start-months-before-renewal",
        title: "Why Contract Review Should Start Months Before Renewal",
      },
      {
        slug: "how-contract-negotiation-shapes-your-salesforce-outcome",
        title: "How Contract Negotiation Shapes Your Salesforce Outcome",
      },
      {
        slug: "salesforce-negotiation-tactics-that-improve-leverage",
        title: "Salesforce Negotiation Tactics That Improve Leverage",
      },
      {
        slug: "salesforce-contract-renewal-risks-to-catch-early",
        title: "Salesforce Contract Renewal Risks to Catch Early",
      },
    ],
  },
  {
    heading: "Salesforce Licensing & Cost Optimisation",
    articles: [
      {
        slug: "how-to-decode-a-salesforce-sku-before-you-buy",
        title: "How to Decode a Salesforce SKU Before You Buy",
      },
      {
        slug: "saas-licensing-decisions-that-create-long-term-waste",
        title: "SaaS Licensing Decisions That Create Long-Term Waste",
      },
      {
        slug: "software-add-ons-that-quietly-inflate-salesforce-spend",
        title: "Software Add-Ons That Quietly Inflate Salesforce Spend",
      },
      {
        slug: "saas-shelfware-the-unused-license-epidemic-and-how-to-stop-it",
        title: "SaaS Shelfware: The Unused License Epidemic and How to Stop It",
      },
      {
        slug: "how-salesforce-software-spend-becomes-hard-to-defend",
        title: "How Salesforce Software Spend Becomes Hard to Defend",
      },
    ],
  },
  {
    heading: "Salesforce Commercial Structures",
    articles: [
      {
        slug: "salesforce-commercial-structures-standard-agreement-vs-sela-vs-aela",
        title: "Salesforce Commercial Structures: Standard Agreement vs. SELA vs. AELA",
      },
      {
        slug: "how-salesforce-sela-pricing-models-affect-renewal-costs",
        title: "How Salesforce SELA Pricing Models Affect Renewal Costs",
      },
      {
        slug: "salesforce-sela-avoiding-bundling-traps-and-securing-decoupled-pricing",
        title: "Salesforce SELA – Avoiding Bundling Traps and Securing Decoupled Pricing",
      },
      {
        slug: "the-blueprint-for-salesforce-aela-what-enterprise-buyers-need-to-know",
        title: "The Blueprint for Salesforce AELA: What Enterprise Buyers Need to Know",
      },
      {
        slug: "salesforce-sela-vs-aela-navigating-the-agentic-ai-cost-paradigm",
        title: "Salesforce SELA vs. AELA: Navigating the Agentic AI Cost Paradigm",
      },
    ],
  },
  {
    heading: "Salesforce Procurement Strategy",
    articles: [
      {
        slug: "how-salesforce-procurement-teams-find-hidden-leverage",
        title: "How Salesforce Procurement Teams Find Hidden Leverage",
      },
      {
        slug: "why-general-procurement-experience-is-not-enough-for-saas",
        title: "Why General Procurement Experience Is Not Enough for SaaS",
      },
      {
        slug: "how-to-govern-salesforce-as-enterprise-software",
        title: "How to Govern Salesforce as Enterprise Software",
      },
      {
        slug: "roadmap-software-spend-optimisation",
        title: "Don't get lost in licences: Build your roadmap for software spend success",
      },
    ],
  },
];

const pillars = [
  {
    title: "Salesforce Renewals",
    body: "Renewal is the one moment where price, term and licence mix are all open at once. The preparation decides the outcome.",
    linkLabel: "Why contract review should start months before renewal",
    slug: "why-contract-review-should-start-months-before-renewal",
  },
  {
    title: "Salesforce Contract Negotiation",
    body: "The commercial conversation is won on data and sequencing, not on volume. We run it with you or behind you.",
    linkLabel: "Our Salesforce negotiation services",
    to: "/services" as const,
  },
  {
    title: "Salesforce Licensing & Cost Optimisation",
    body: "Editions, SKUs and add-ons drift away from how teams actually work. Correcting that drift is where most savings sit.",
    linkLabel: "How to decode a Salesforce SKU before you buy",
    slug: "how-to-decode-a-salesforce-sku-before-you-buy",
  },
  {
    title: "Salesforce Commercial Strategy",
    body: "A view of the next three years — spend trajectory, term structure and what you want to keep negotiable.",
    linkLabel: "How Salesforce procurement teams find hidden leverage",
    slug: "how-salesforce-procurement-teams-find-hidden-leverage",
  },
  {
    title: "Salesforce SELA",
    body: "An enterprise-wide bundled agreement. Useful at genuine scale, costly when entitlements outrun usage.",
    linkLabel: "Salesforce SELA advisory",
    to: "/sela" as const,
  },
  {
    title: "Salesforce AELA",
    body: "The agreement type covering Agentforce and consumption-based AI activity rather than seats.",
    linkLabel: "Salesforce AELA advisory",
    to: "/aela" as const,
  },
];

const steps = [
  {
    n: "01",
    title: "Assess",
    body: (
      <>
        Review the existing commercial and licensing position, using{" "}
        <Link
          to="/dioro"
          className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
        >
          Dioro
        </Link>{" "}
        for usage insight.
      </>
    ),
  },
  {
    n: "02",
    title: "Understand",
    body: "Assess usage, requirements and future needs.",
  },
  {
    n: "03",
    title: "Strategise",
    body: "Identify commercial priorities, opportunities and risks.",
  },
  {
    n: "04",
    title: "Negotiate",
    body: "Support the buyer through the commercial discussion.",
  },
];

export const Route = createFileRoute("/salesforce-procurement")({
  head: () => ({
    meta: [
      { title: pageTitle },
      { name: "description", content: searchDescription },
      { property: "og:title", content: pageTitle },
      { property: "og:description", content: searchDescription },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://saased.co/salesforce-procurement" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: pageTitle },
      { name: "twitter:description", content: searchDescription },
    ],
    links: [
      { rel: "canonical", href: "https://saased.co/salesforce-procurement" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(jsonLd) },
    ],
  }),
  component: SalesforceProcurementPage,
});

function SalesforceProcurementPage() {
  return (
    <>
      {/* HERO + ENTITY DEFINITION */}
      <section className="border-b border-brand-ink/10 bg-brand-primary py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col gap-6 md:gap-8">
          <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
            Salesforce Procurement
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Salesforce Procurement &amp; Commercial Advisory
          </h1>
          <p className="max-w-3xl font-sans text-base md:text-lg leading-relaxed text-brand-ink/85">
            SaaSed is a specialist Salesforce procurement and commercial
            advisory company. We work with enterprise buyers on Salesforce
            renewals, contract negotiation, licensing decisions, commercial
            strategy and cost optimisation — with the aim of improving
            commercial control and removing cost that serves no purpose.
          </p>
        </div>
      </section>

      {/* WHAT IS SALESFORCE PROCUREMENT */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              01 · Definition
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              What Is Salesforce Procurement?
            </h2>
          </div>
          <div className="mt-12 space-y-6 font-sans text-base leading-relaxed text-brand-ink/85">
            <p style={{ marginBottom: '24px' }}>
              Salesforce procurement is the commercial discipline of buying and
              renewing Salesforce: understanding the agreement already in place,
              reviewing what is licensed, comparing that against how the
              business actually works today and expects to work next year, then
              evaluating the commercial options available before a renewal is
              signed.
            </p>
            <p>
              In practice it runs in that order — position, requirement, option,
              negotiation. Skipping the first two steps is what leaves buyers
              responding to a vendor proposal rather than shaping one. For the
              question-by-question detail, see{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "what-salesforce-customers-should-ask-before-renewal" }}
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                what Salesforce customers should ask before renewal
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* WHY SPECIALIST EXPERTISE */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              02 · Specialisation
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Why Salesforce Procurement Requires Specialist Expertise
            </h2>
          </div>
          <div className="mt-12 space-y-6 font-sans text-base leading-relaxed text-brand-ink/85">
            <p style={{ marginBottom: '24px' }}>
              Generic software procurement assumes comparable products, visible
              list prices and substitutable suppliers. Salesforce agreements
              behave differently: pricing sits inside bundles and multi-year
              structures, editions and add-ons interact, and the renewal calendar
              is a commercial instrument in its own right.
            </p>
            <p>
              That combination rewards pattern recognition. Knowing which SKU
              names carry which entitlements, which clauses matter three years
              out, and how a proposal is typically constructed is Salesforce
              knowledge, not procurement knowledge.{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "why-general-procurement-experience-is-not-enough-for-saas" }}
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                Why general procurement experience is not enough for SaaS
              </Link>{" "}
              goes further into that gap.
            </p>
          </div>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section className="border-b border-brand-ink/10 bg-brand-sand/40">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-12">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              03 · Scope
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              What SaaSed Helps Enterprise Buyers With
            </h2>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              Six areas, each with a dedicated resource. The engagement detail
              lives on our{" "}
              <Link
                to="/services"
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                Salesforce procurement services
              </Link>{" "}
              page.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="flex h-full flex-col justify-between gap-6 rounded-2xl border border-brand-ink/10 bg-brand-cream p-8"
              >
                <div className="flex flex-col gap-4">
                  <h3 className="font-serif text-xl leading-tight text-brand-ink">
                    {p.title}
                  </h3>
                  <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                    {p.body}
                  </p>
                </div>
                {p.to ? (
                  <Link
                    to={p.to}
                    className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/70 hover:text-brand-punch"
                  >
                    {p.linkLabel}
                    <ArrowIcon />
                  </Link>
                ) : (
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug! }}
                    className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/70 hover:text-brand-punch"
                  >
                    {p.linkLabel}
                    <ArrowIcon />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMERCIAL STRUCTURES */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              04 · Structures
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Salesforce Commercial Structures
            </h2>
          </div>
          <div className="mt-12 space-y-6 font-sans text-base leading-relaxed text-brand-ink/85">
            <p style={{ marginBottom: '24px' }}>
              <strong>Standard agreements</strong> license named products and
              user counts on order forms — granular, easy to benchmark, and
              renewed line by line.
            </p>
            <p style={{ marginBottom: '24px' }}>
              <strong>SELA</strong> replaces that granularity with one
              enterprise-wide commitment across a portfolio. Depth on the
              structure, its risks and how to negotiate it sits on our{" "}
              <Link
                to="/sela"
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                Salesforce SELA advisory
              </Link>{" "}
              page.
            </p>
            <p style={{ marginBottom: '24px' }}>
              <strong>AELA</strong> covers Agentforce and agent activity, where
              the unit of consumption is not a seat. The detail is on our{" "}
              <Link
                to="/aela"
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                Salesforce AELA advisory
              </Link>{" "}
              page.
            </p>
            <p>
              For a side-by-side comparison of the three, read{" "}
              <Link
                to="/blog/$slug"
                params={{ slug: "salesforce-commercial-structures-standard-agreement-vs-sela-vs-aela" }}
                className="underline decoration-2 underline-offset-4 hover:text-brand-punch"
              >
                Salesforce Commercial Structures: Standard Agreement vs. SELA vs. AELA
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* WHEN TO ENGAGE */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-12">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              05 · Timing
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              When Should You Engage a Salesforce Procurement Advisor?
            </h2>
          </div>
          <ul className="grid gap-6 md:grid-cols-2 font-sans text-base leading-relaxed text-brand-ink/85">
            {[
              "A Salesforce renewal is approaching and the licence position has not been reviewed yet.",
              "A proposal or quote is on the table and you want it read commercially before responding.",
              "Salesforce spend has grown faster than the business case behind it.",
              "The licence estate has become difficult to explain, let alone manage.",
              "A SELA or AELA is being offered and the structure needs assessing before commitment.",
              "Your procurement team is strong but has no Salesforce-specific reference points.",
              "A major expansion, consolidation or multi-year commitment is being considered.",
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <span className="text-brand-punch mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* APPROACH */}
      <section className="border-b border-brand-ink/10 bg-brand-sand/40">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-12">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              06 · Approach
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              How SaaSed Approaches Salesforce Procurement
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {steps.map((s) => (
              <div
                key={s.n}
                className="flex h-full flex-col gap-4 rounded-2xl border border-brand-ink/10 bg-brand-cream p-8"
              >
                <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/60">
                  {s.n}
                </p>
                <h3 className="font-serif text-xl leading-tight text-brand-ink">
                  {s.title}
                </h3>
                <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-10">
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Salesforce Procurement Insights
            </h2>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              Our existing analysis, grouped by the part of the procurement
              cycle it belongs to.
            </p>
          </div>
          <div className="flex flex-col gap-12">
            {insightGroups.map((group) => (
              <div key={group.heading} className="flex flex-col gap-6">
                <h3 className="font-serif text-xl md:text-2xl leading-tight text-brand-ink">
                  {group.heading}
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                  {group.articles.map((article) => (
                    <li key={article.slug}>
                      <Link
                        to="/blog/$slug"
                        params={{ slug: article.slug }}
                        title={article.title}
                        aria-label={`Read ${article.title}`}
                        className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-brand-ink/10 bg-brand-cream p-8 transition-colors hover:border-brand-punch/40"
                      >
                        <span className="font-serif text-lg leading-tight text-brand-ink">
                          {article.title}
                        </span>
                        <span className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/70 group-hover:text-brand-punch">
                          Read the analysis
                          <ArrowIcon />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <h2 className="font-serif text-2xl md:text-3xl leading-tight mb-10">
            Salesforce Procurement Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqData.map((item, i) => (
              <AccordionItem key={item.question} value={`item-${i}`} className="[&>[role=region][data-state=closed]]:hidden">
                <AccordionTrigger className="font-sans text-base md:text-lg font-medium text-brand-ink">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent forceMount className="font-sans text-base leading-relaxed text-brand-ink/80">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <DarkCtaSection
        label="Next Step"
        title="Preparing for a Salesforce Renewal?"
        description="Bring in an independent commercial review while the terms are still open. We will tell you what your position looks like from the other side of the table."
        buttonText="Request an independent review"
        buttonClassName="rounded-sm"
      />
    </>
  );
}
